import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Badge, RotateCcw, CheckCircle, PlayCircle, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import ConfidenceSlider from '../../../components/shared/ConfidenceSlider';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import { blufBank } from '../../../data/BLUFBank';
import { withKinematicSensors } from '../../../core/hocs/withKinematicSensors'; // NEW INJECTION

// Inject HOC props into the signature
const EmailTriageGameBase = ({ gameData, onComplete, onBack, resetKinematics, getKinematicMetrics, startHover, endHover }) => {
  const { id, preGameBriefing, failureLogic } = gameData;

  const { playSuccess, playError, playWin, playExtinguish } = useGameSounds();
  
  // UX Calibration: Stable initialization with Fisher-Yates array shuffle.
  // Securely caches exactly 5 unpredictable scenarios per session.
  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
      let array = [...blufBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 5);
  }

  const activeDeck = deckRef.current;
  
  // --- STATE ---
  const [stage, setStage] = useState('briefing'); // 'briefing', 'pre', 'playing', 'intervention', 'results'
  const [confidencePre, setConfidencePre] = useState(5);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [sessionAttempts, setSessionAttempts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // NEW INJECTION

  // ADA Compliance: Silent telemetry background tracker
  const sessionStartRef = useRef(Date.now());
  const [shuffledOptions, setShuffledOptions] = useState([]);

  // --- 1. INITIALIZATION ---
  useEffect(() => {
    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('pre');
    }
  }, [preGameBriefing]);

  useEffect(() => {
      if (activeDeck[currentIndex]) {
          const opts = [...activeDeck[currentIndex].options];
          setShuffledOptions(opts.sort(() => Math.random() - 0.5));
      }
  }, [currentIndex, activeDeck]);

  // --- 2. START GAME ---
  const handleStartGame = () => {
      sessionStartRef.current = Date.now();
      resetKinematics(); // Anchor TTFI clock
      setStage('playing');
  };

  // --- 3. FAILURE LOGIC ---
  const triggerFailure = () => {
      playExtinguish();
      const logic = failureLogic?.['communication_breakdown'] || {
          title: "Communication Breakdown",
          script: "You sent a deeply unprofessional or confusing email. In the enterprise, clarity is currency. Review the feedback and try again.",
          videoUrl: null
      };
      setActiveIntervention(logic);
      setStage('intervention');
  };

  const handleRetry = () => {
      // TELEMETRY PATCH: Push failure iteration to session history
      setSessionAttempts(prev => [...prev, { status: 'retry', score: totalScore, timestamp: Date.now() }]);

      // Re-shuffle for retry to eliminate muscle memory
      let array = [...blufBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 5);

      setCurrentIndex(0);
      setCurrentFeedback(null);
      setTotalScore(0);
      setActiveIntervention(null);
      
      resetKinematics(); // Anchor TTFI clock
      setStage('playing');
  };

  // --- 4. GAMEPLAY HANDLERS ---
  const handleSelectOption = (option) => {
    if (currentFeedback) return; 

    const metrics = getKinematicMetrics();
    const responseText = option.rationale;
    setTotalScore(prev => prev + option.score);

    const activeCard = activeDeck[currentIndex];

    // SECURE KINEMATIC PAYLOAD
    const interactionRecord = {
        scenarioId: activeCard.id,
        selectedCategory: option.type,
        isCorrect: option.type === 'Optimal',
        scoreAwarded: option.score,
        ttfiMs: metrics.ttfi,
        jiggleCount: metrics.jiggleCount,
        tabSwitches: metrics.tabSwitches,
        hoverDurationsMs: metrics.hoverDurationsMs,
        timestamp: Date.now()
    };

    setSessionAttempts(prev => [...prev, interactionRecord]);
    resetKinematics(); // Reset sensors for the next scenario

    // Enforce Tri-State Color Hierarchy & Timing Validation
    if (option.type === 'Optimal') {
        playSuccess();
        setCurrentFeedback({ type: 'success', text: responseText, score: option.score });
        nextCard(4000); // ALIGNED TO 4000ms
    } else if (option.type === 'Suboptimal') {
        playSuccess(); 
        setCurrentFeedback({ type: 'warning', text: responseText, score: option.score });
        nextCard(4000); // ALIGNED TO 4000ms
    } else {
        playError();
        setCurrentFeedback({ type: 'error', text: responseText, score: option.score });
        setTimeout(() => {
            triggerFailure();
        }, 4000); // ALIGNED TO 4000ms
    }
  };

  const nextCard = (delay = 1000) => {
      setTimeout(() => {
          setCurrentFeedback(null);
          if (currentIndex + 1 < activeDeck.length) {
              setCurrentIndex(i => i + 1);
          } else {
              playWin();
              setStage('results');
          }
      }, delay);
  };

  const handleFinalSubmit = async () => {
      const percentage = Math.round((totalScore / (activeDeck.length * 100)) * 100);
      const payload = {
          id,
          rawInteractions: [
              ...sessionAttempts,
              { status: 'success', score: percentage, timestamp: Date.now(), durationMs: Date.now() - sessionStartRef.current }
          ]
      };
      
      if (typeof onComplete === 'function') {
          try {
              setIsSubmitting(true);
              await onComplete(payload);
          } catch (error) {
              console.error("Submission Failed:", error);
              if (error.message?.includes('network') || error.code === 'unavailable') {
                  alert("Network connection suspended by the browser. Please refresh the page and try again.");
              } else {
                  alert("An error occurred saving your data. Please try again.");
              }
          } finally {
              setIsSubmitting(false);
          }
      }
  };

  const activeCard = activeDeck[currentIndex];

  return (
    <div className="h-full flex flex-col bg-slate-50 rounded-xl overflow-hidden border border-gray-200 relative min-h-[500px]">
      
      {/* --- OVERLAYS --- */}
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay type="briefing" data={preGameBriefing} onContinue={() => setStage('pre')} />
            </div>
        )}
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay type="intervention" data={activeIntervention} onRetry={handleRetry} />
            </div>
        )}
      </AnimatePresence>

      {/* --- STAGE 1: BASELINE (DIFFICULTY) --- */}
      {stage === 'pre' && (
          <motion.div key="pre" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-grow flex items-center justify-center p-6 bg-white z-50 absolute inset-0">
            <div className="max-w-md w-full">
              <ConfidenceSlider value={confidencePre} onChange={setConfidencePre} title="Baseline Assessment" subtitle="How confident are you in drafting high-stakes corporate emails?" />
              <button onClick={handleStartGame} className="mt-8 w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition transform hover:scale-105"><PlayCircle /> START DRAFTING</button>
            </div>
          </motion.div>
      )}

      {/* --- RESULTS SCREEN --- */}
      {stage === 'results' && (
        <div className="p-8 flex flex-col items-center justify-center text-center h-full animate-fade-in absolute inset-0 bg-white z-50">
            <div className="mb-6 p-4 bg-green-100 rounded-full text-green-600">
                <Badge size={48} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Draft Complete!</h3>
            
            {/* Max score is length * 100 */}
            <p className="text-gray-600 mb-6">Score: <span className="font-bold text-blue-600">{totalScore} / {activeDeck.length * 100}</span></p>

            <div className="flex gap-4">
                <button onClick={handleRetry} className="text-gray-500 font-bold hover:text-gray-800">Draft Again</button>
                <button 
                  onClick={handleFinalSubmit} 
                  disabled={isSubmitting} 
                  className="bg-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:bg-blue-700 transition transform hover:scale-105 flex items-center justify-center disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
                  Complete Mission
                </button>
            </div>
        </div>
      )}

      {/* --- PLAYING SCREEN --- */}
      {stage === 'playing' && (
        <div className="flex flex-col h-full bg-white absolute inset-0">
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center shadow-sm z-10 bg-slate-50">
                <h3 className="text-sm font-bold text-gray-800">{gameData.preGameBriefing?.title || "Drafting Mode"}</h3>
                <div className="flex items-center gap-2">
                    {/* ADA COMPLIANCE PATCH: Visual countdown timer completely removed. */}
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Session</span>
                </div>
            </div>

            <div className="p-6 flex flex-col flex-grow bg-white overflow-y-auto relative">
                
                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    <span>Drafting Progress</span>
                    <span>Scenario {currentIndex + 1} / {activeDeck.length}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${((currentIndex + 1) / activeDeck.length) * 100}%` }}></div>
                    </div>
                </div>

                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <p className="text-xs font-black uppercase tracking-widest text-blue-600 mb-1">Scenario</p>
                    <p className="text-gray-700 font-medium leading-relaxed">{activeCard?.scenarioContext}</p>
                </div>
                
                {/* Options Grid */}
                <div className="flex-1 space-y-4">
                    {shuffledOptions.map((opt, index) => {
                        return (
                        <button
                            key={index}
                            onClick={() => handleSelectOption(opt)}
                            onMouseEnter={() => startHover(`opt_${index}`)}
                            onMouseLeave={() => endHover(`opt_${index}`)}
                            disabled={!!currentFeedback}
                            className="w-full text-left p-5 border-2 rounded-xl transition-all font-medium bg-white border-slate-200 hover:border-blue-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                            <div className="mb-2">
                                <span className="text-xs font-black uppercase tracking-widest text-slate-400 mr-2">Subject:</span>
                                <span className="text-sm font-bold text-slate-800">{opt.subject}</span>
                            </div>
                            <div className="text-sm text-slate-600 mb-2 leading-snug">
                                {opt.opener}
                            </div>
                            <div className="text-sm text-slate-600 leading-snug">
                                {opt.ask}
                            </div>
                        </button>
                        );
                    })}
                </div>

                {/* Instant Feedback (Dynamic Color Logic) */}
                <AnimatePresence>
                    {currentFeedback && (
                        <motion.div 
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`absolute inset-0 z-50 flex items-center justify-center rounded-2xl bg-white/95 backdrop-blur-md border-4 p-6 
                                ${currentFeedback.type === 'success' ? 'border-green-500' :
                                  currentFeedback.type === 'warning' ? 'border-amber-500' : 'border-red-500'}`}
                        >
                            <div className="text-center flex flex-col items-center">
                                {currentFeedback.type === 'success' ? <CheckCircle className="w-16 h-16 text-green-600 mb-4" /> :
                                 currentFeedback.type === 'warning' ? <AlertTriangle className="w-16 h-16 text-amber-500 mb-4" /> :
                                 <XCircle className="w-16 h-16 text-red-600 mb-4" />}

                                <h3 className={`text-xl font-black uppercase mb-2
                                    ${currentFeedback.type === 'success' ? 'text-green-700' :
                                      currentFeedback.type === 'warning' ? 'text-amber-700' : 'text-red-700'}`}>
                                    {currentFeedback.type === 'success' ? 'Optimal' :
                                     currentFeedback.type === 'warning' ? 'Suboptimal' : 'Poor Choice'}
                                </h3>
                                <p className={`font-medium text-sm leading-snug
                                    ${currentFeedback.type === 'success' ? 'text-green-900' :
                                      currentFeedback.type === 'warning' ? 'text-amber-900' : 'text-red-900'}`}>
                                    {currentFeedback.text}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="p-4 bg-white border-t flex justify-between z-20">
                <button onClick={onBack} className="text-gray-500 font-bold hover:text-gray-800">Back</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default withKinematicSensors(EmailTriageGameBase);